/**
 * TipTap Document Update Strategy
 *
 * Defines which tools trigger document updates and how to convert
 * tool outputs to TipTap operations.
 */

import { TipTapPatch, TipTapToolOutput } from "../DocumentAgentLGTypes";
import { CoreOperation } from "../../../Components/TipTapEditor/operations";

// ============================================================================
// TYPES
// ============================================================================

export interface UpdateStrategyConfig {
  // Tools that trigger document updates
  documentUpdateTools: string[];
  // Tools that create new documents
  documentCreationTools: string[];
  // Tools that only view/read documents
  readOnlyTools: string[];
}

export interface UpdateStrategy {
  shouldHandleTool: (toolName: string) => boolean;
  isDocumentCreation: (toolName: string) => boolean;
  isReadOnly: (toolName: string) => boolean;
  extractPatches: (toolOutput: TipTapToolOutput) => TipTapPatch[];
  extractPatch: (toolOutput: TipTapToolOutput) => TipTapPatch | null;
}

// ============================================================================
// DEFAULT CONFIGURATION
// ============================================================================

const DEFAULT_CONFIG: UpdateStrategyConfig = {
  documentUpdateTools: [
    "update_document",
    "add_comment",
    "find_and_replace",
    "manage_track_changes",
    "manage_comments",
    "edit_claims",
    "update_specification",
    "draft_title_foi_background_summary",
    "draft_summary",
    "draft_brief_description_of_drawings",
    "draft_detailed_description",
    "draft_detailed_description_for_single_figure",
    "create_claims",
    "update_claims",
    "add_paragraph_numbers",
    "check_specification_and_claims_document",
  ],
  documentCreationTools: [
    "create_document",
    "initialize_specification",
    "initialize_claims",
  ],
  readOnlyTools: [
    "view_document",
    "read_section",
    "get_document_info",
    "list_documents",
    "search_documents",
    "get_document_outline",
  ],
};

// ============================================================================
// FACTORY
// ============================================================================

export function createTipTapUpdateStrategy(
  config: Partial<UpdateStrategyConfig> = {}
): UpdateStrategy {
  const mergedConfig: UpdateStrategyConfig = {
    ...DEFAULT_CONFIG,
    ...config,
  };

  const allHandledTools = new Set([
    ...mergedConfig.documentUpdateTools,
    ...mergedConfig.documentCreationTools,
  ]);
  const creationTools = new Set(mergedConfig.documentCreationTools);
  const readOnlyToolsSet = new Set(mergedConfig.readOnlyTools);

  return {
    shouldHandleTool(toolName: string): boolean {
      return allHandledTools.has(toolName);
    },

    isDocumentCreation(toolName: string): boolean {
      return creationTools.has(toolName);
    },

    isReadOnly(toolName: string): boolean {
      return readOnlyToolsSet.has(toolName);
    },

    extractPatches(toolOutput: TipTapToolOutput): TipTapPatch[] {
      if (toolOutput.patches && toolOutput.patches.length > 0) {
        return toolOutput.patches;
      }
      const single = this.extractPatch(toolOutput);
      return single ? [single] : [];
    },

    extractPatch(toolOutput: TipTapToolOutput): TipTapPatch | null {
      // Direct patch from tool output
      if (toolOutput.patch) {
        return toolOutput.patch;
      }

      // Try to construct patch from legacy format
      // This handles backward compatibility with tools that return HTML operations
      const rawOutput = toolOutput as any;

      if (rawOutput.docId && rawOutput.operations) {
        return {
          documentId: rawOutput.docId,
          operations: rawOutput.operations as CoreOperation[],
        };
      }

      // If tool output has content updates in a different format
      if (rawOutput.docId && rawOutput.content) {
        // Create a replace operation for the entire document
        // This is a fallback for simple content updates
        const operations: CoreOperation[] = [
          {
            op: "replace",
            target: { nodeId: "root" },
            content: rawOutput.content,
          } as CoreOperation,
        ];

        return {
          documentId: rawOutput.docId,
          operations,
        };
      }

      return null;
    },
  };
}

// ============================================================================
// PATENT SPECIFICATION STRATEGY
// ============================================================================

export function createPatentSpecificationStrategy(): UpdateStrategy {
  return createTipTapUpdateStrategy({
    documentUpdateTools: [
      "update_document",
      "draft_title_foi_background_summary",
      "draft_summary",
      "draft_brief_description_of_drawings",
      "draft_detailed_description",
      "draft_detailed_description_for_single_figure",
      "add_paragraph_numbers",
      "check_specification_and_claims_document",
      "update_specification_document",
    ],
    documentCreationTools: ["initialize_specification"],
    readOnlyTools: ["view_specification", "view_document"],
  });
}

// ============================================================================
// PATENT CLAIMS STRATEGY
// ============================================================================

export function createPatentClaimsStrategy(): UpdateStrategy {
  return createTipTapUpdateStrategy({
    documentUpdateTools: [
      "update_document",
      "create_claims",
      "edit_claims",
      "update_claims",
    ],
    documentCreationTools: ["initialize_claims"],
    readOnlyTools: ["view_claims", "view_document"],
  });
}

// ============================================================================
// GENERAL PURPOSE STRATEGY
// ============================================================================

export function createGeneralPurposeStrategy(): UpdateStrategy {
  return createTipTapUpdateStrategy({
    documentUpdateTools: [
      "update_document",
      "add_comment",
      "find_and_replace",
      "manage_track_changes",
      "manage_comments",
    ],
    documentCreationTools: ["create_document"],
    readOnlyTools: [
      "view_document",
      "read_section",
      "get_document_info",
      "list_documents",
      "search_documents",
      "get_document_outline",
    ],
  });
}
